import { useMemo, useRef, useState } from "react";

export type SortDirection = "asc" | "desc";

export interface SortState<K extends string> {
  key: K;
  direction: SortDirection;
}

/** A column's sortable value extractor. `null`/`undefined` sorts to the end regardless of direction. */
export type SortAccessors<T, K extends string> = Record<
  K,
  (row: T) => string | number | null | undefined
>;

function compareValues(
  a: string | number | null | undefined,
  b: string | number | null | undefined
): number {
  const aMissing = a === null || a === undefined;
  const bMissing = b === null || b === undefined;
  if (aMissing && bMissing) return 0;
  // Missing values always sort to the end — regardless of asc/desc — the caller
  // (which flips the overall comparator for "desc") relies on this function
  // never itself being direction-aware for missing values.
  if (aMissing) return 1;
  if (bMissing) return -1;
  if (typeof a === "string" && typeof b === "string") {
    return a.localeCompare(b);
  }
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

/**
 * Client-side sort over an already-fully-loaded row array (never over a single
 * page of a server-paginated list — that would silently sort only the visible
 * page and mislead the operator; see `git-file-tracking` sibling rule in the
 * admin panel's own conventions doc).
 *
 * Clicking a NEW column starts descending; clicking the already-active column
 * again flips direction (PK 2026-07-25: this panel is dominated by counts/
 * costs/dates, where "biggest/newest first" is the useful first click, not
 * ascending). The initial (no-click) order is `defaultSort` — every table
 * must pick a default that is useful without any interaction.
 */
export function useSortableRows<T, K extends string>(
  rows: T[] | null | undefined,
  accessors: SortAccessors<T, K>,
  // `NoInfer` keeps `defaultSort`'s single-key literal from hijacking inference
  // of K — without it, TS collapses K to whichever literal `defaultSort.key`
  // happens to be (e.g. "usageCount") instead of the full union of columns
  // from `accessors`, and every `SortableTh sortKey="..."` for every OTHER
  // column in the table fails to type-check (caught 2026-07-25: `tsc --build`
  // was red across every page using this hook even though Jest, which
  // type-checks nothing, was green).
  defaultSort: SortState<NoInfer<K>>
): {
  sorted: T[];
  sort: SortState<K>;
  toggleSort: (key: K) => void;
} {
  const [sort, setSort] = useState<SortState<K>>(defaultSort);

  // Every call site passes an inline accessors object literal, so its identity
  // changes on every render — it can never be an honest `useMemo` dependency.
  // Mirrors the `fetcherRef` pattern in `useApi.ts`: a ref decouples the
  // memo's re-run trigger (`rows`/`sort`, the values that actually determine
  // the sorted output) from that per-render churn, while `sorted` below always
  // reads the *latest* accessors — never a stale closure.
  const accessorsRef = useRef(accessors);
  accessorsRef.current = accessors;

  const toggleSort = (key: K) => {
    setSort((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "desc" };
    });
  };

  const sorted = useMemo(() => {
    const source = rows ?? [];
    const accessor = accessorsRef.current[sort.key];
    const copy = [...source];
    copy.sort((rowA, rowB) => {
      const cmp = compareValues(accessor(rowA), accessor(rowB));
      // Missing-value placement (always last) must not flip with direction —
      // only invert the sign when both values are present.
      if (cmp === 0) return 0;
      const aVal = accessor(rowA);
      const bVal = accessor(rowB);
      const eitherMissing =
        aVal === null || aVal === undefined || bVal === null || bVal === undefined;
      if (eitherMissing) return cmp;
      return sort.direction === "asc" ? cmp : -cmp;
    });
    return copy;
    // `accessorsRef.current` is read here (see comment above) — a ref, so
    // react-hooks/exhaustive-deps does not require it in the dependency
    // array; `rows`/`sort` are the only values that should re-trigger this.
  }, [rows, sort]);

  return { sorted, sort, toggleSort };
}
